import React from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useStore } from '@/store';

const STEPS = [
  { step: 1, label: 'Search' },
  { step: 2, label: 'Flights' },
  { step: 3, label: 'Seats' },
  { step: 4, label: 'Details' },
  { step: 5, label: 'Payment' },
];

interface Props {
  currentStep: number;
}

export function StepIndicator({ currentStep }: Props) {
  const navigate = useNavigate();
  const { selectedFlight, setCurrentStep } = useStore();

  const progress = Math.max(0, Math.min(1, (currentStep - 1) / (STEPS.length - 1)));

  function isUnlocked(step: number): boolean {
    return step <= currentStep;
  }

  function getRoute(step: number): string | null {
    if (step === 3) return selectedFlight ? `/seats/${selectedFlight.flightId}` : null;
    const routes: Record<number, string> = { 1: '/search', 2: '/results', 4: '/passengers', 5: '/payment' };
    return routes[step] ?? null;
  }

  function handleStepClick(step: number) {
    if (!isUnlocked(step) || step === currentStep) return;
    const route = getRoute(step);
    if (route) {
      setCurrentStep(step);
      navigate(route);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 pb-3">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          {STEPS.map((s) => {
            const state = s.step < currentStep ? 'done' : s.step === currentStep ? 'current' : 'upcoming';
            const unlocked = isUnlocked(s.step);
            const clickable = unlocked && s.step !== currentStep;

            return (
              <button
                key={s.step}
                onClick={() => handleStepClick(s.step)}
                disabled={!clickable}
                aria-current={s.step === currentStep ? 'step' : undefined}
                className={clsx(
                  'flex items-center gap-1.5 min-w-0 focus-ring rounded',
                  clickable ? 'cursor-pointer hover:opacity-70 transition-opacity' : 'cursor-default',
                )}
              >
                <span
                  className={clsx(
                    'text-[11px] font-mono tabular-nums',
                    state === 'current'  && 'text-brand-600 font-semibold',
                    state === 'done'     && 'text-brand-600',
                    state === 'upcoming' && 'text-soft',
                  )}
                  aria-hidden="true"
                >
                  {String(s.step).padStart(2, '0')}
                </span>
                <span
                  className={clsx(
                    'text-xs truncate',
                    state === 'current'  && 'text-strong font-semibold',
                    state === 'done'     && 'text-muted',
                    state === 'upcoming' && 'text-soft',
                  )}
                >
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative h-[2px] rounded-full bg-surface-divider overflow-visible">
          <div
            className="absolute inset-y-0 left-0 bg-brand-500 rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${progress * 100}%` }}
            aria-hidden="true"
          />
          {/* Plane at the tip of progress */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-[left] duration-300 ease-out"
            style={{ left: `${progress * 100}%` }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-brand-500 rotate-90">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </div>
        </div>

        <span className="sr-only">
          Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1]?.label}
        </span>
      </div>
    </div>
  );
}
